import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import classnames from 'classnames';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import config from '../../../app/config';
import { color } from '../../../app/theme';
import { ModalType } from '../../../app/types';
import { TWD } from '../../../app/utils';
import IconDelete from '../../../assets/images/Icons/IconDelete';
import icHelpCircle from '../../../assets/images/svg/ic-help-circle.svg';
import {
  addOrUpdateCorrection,
  checkData,
  deleteCorrection,
  getCorrectionLisContinue,
  getCorrectionListInit,
  getPoints,
  simulate,
} from '../../../features/aiBotSlice';
import { closeModal, openModal } from '../../../features/modalSlice';
import EmojiPicker from '../../shared/EmojiPicker';
import TextField from '../../shared/TextField';
import Tooltip from '../../shared/Tooltip';
import PageTitle from '../PageTitle';
import CorrectionModal from './CorrectionModal';
import CorrectionRecords from './CorrectionRecords';
import MessageContent from './MessageContent';

const useStyles = makeStyles(
  {
    root: {
      position: 'relative',
    },
    title: {
      display: 'flex',
      alignItems: 'center',
      '& > img': {
        marginLeft: 4,
      },
    },
    chatBlock: {
      borderRadius: '16px',
      border: `1px solid ${color.$8}`,
      boxShadow: '0px 8px 36px 0px rgba(0, 0, 0, 0.06)',
    },
    chatHeader: {
      backgroundColor: color.$3,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      padding: '16px 24px',
      borderTopRightRadius: '16px',
      borderTopLeftRadius: '16px',
      fontSize: '15px',
      fontStyle: 'normal',
      fontWeight: '500',
      lineHeight: '15px',
      color: color.$7,
    },
    chatContent: {
      padding: '16px 24px 24px',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$12,
    },
    clear: {
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
    },
    input: {
      '& > div > textarea': {
        paddingRight: 16,
      },
      '& > div > textarea::placeholder': {
        paddingTop: 3,
      },
    },
    chatInput: {
      marginTop: 16,
      position: 'relative',
    },
    aiLoading: {
      position: 'absolute',
      top: '-24px',
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: 'normal',
      color: color.$10,
    },
    endAdornment: {
      paddingTop: '4px',
      paddingRight: '15px',
    },
    point: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'end',
      color: color.$12,
      marginBottom: 4,
      marginTop: 8,
      '& > span': {
        border: `1px solid ${color.$10}`,
        padding: '2px 6px',
        borderRadius: 8,
      },
      '& > a': {
        color: color.$2,
        textDecorationLine: 'underline',
      },
    },
    tip: {
      textAlign: 'right',
      color: color.$17,
    },
    notAllowed: {
      cursor: 'not-allowed',
    },
    delete: {
      marginLeft: 4,
    },
    correctionRecords: {
      fontSize: '13px',
      fontStyle: 'normal',
      fontWeight: '400',
      lineHeight: '13px',
      color: color.$2,
      textDecorationLine: 'underline',
      marginBottom: 36,
      marginLeft: 8,
      cursor: 'pointer',
    },
    dfc: {
      display: 'flex',
      alignItems: 'center',
    },
    red: {
      color: color.$17,
    },
  },
  {
    name: 'TestingContainer',
  },
);

const TestingContainer = ({ pathKey }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { orgId, id } = useParams();
  const { data, progress, isLoading, correctionList, tuningUsed } = useSelector(
    store => store.aiBot,
  );
  const { displayName, nextResource } = useSelector(
    store => store.organization,
  );

  const [chatInput, setChatInput] = useState('');
  const [messages, setMessages] = useState([]);
  const [isSimulatimg, setIsSimulatimg] = useState(false);
  const [totalPoints, setTotalPoints] = useState(0);
  const [isShowCorrectionRecords, setIsShowCorrectionRecords] = useState(false);
  const [isShowCorrectionModal, setIsShowCorrectionModal] = useState(false);
  const [selectQA, setSelectQA] = useState(false);

  const chartInputRef = useRef();

  const quota = nextResource?.resources?.ai_tuning?.quota || 0;

  const isDataVerify = checkData(data, progress);

  const handleClear = () => {
    if (!isSimulatimg) {
      setMessages([]);
    }
  };

  const submitHandler = async () => {
    if (chatInput.trim().length === 0) {
      return;
    }
    setIsSimulatimg(true);
    setChatInput('');
    setMessages([...messages, { income: chatInput }]);

    const result = await dispatch(simulate(orgId, id, chatInput));

    if (result) {
      setTotalPoints(result?.pointLeft || 0);
      setMessages([
        ...messages,
        { income: chatInput, outcome: result?.answer },
      ]);
    }

    setIsSimulatimg(false);
  };

  const textChangeHandler = event => {
    setChatInput(event.target.value);
  };

  const onEmojiClick = emoji => {
    const startPos = chartInputRef.current?.selectionStart;
    const endPos = chartInputRef.current?.selectionEnd;

    const newValue =
      chartInputRef.current.value.substring(0, startPos) +
      emoji +
      chartInputRef.current.value.substring(endPos);
    setChatInput(newValue);
    chartInputRef.current.focus();
    chartInputRef.current.selectionStart = startPos + 2;
    chartInputRef.current.selectionEnd = endPos + 2;
  };

  const handleDeleteCorrection = async v => {
    dispatch(
      openModal({
        modalType: ModalType.ALERT_MODAL,
        modalProps: {
          title: t(`${pathKey}:deleteDataset-modal-title`),
          content: t(`${pathKey}:deleteDataset-modal-content`),
          onCancel: () => {
            dispatch(closeModal());
          },
          onClick: async () => {
            await dispatch(deleteCorrection(orgId, id, v));
            setIsShowCorrectionModal(false);
            dispatch(closeModal());
          },
          cancelText: t('modal.cancel'),
          submitText: t('modal.sure'),
        },
      }),
    );
  };

  const handleAddCorrection = async v => {
    await dispatch(addOrUpdateCorrection(orgId, id, v));
    setIsShowCorrectionModal(false);
  };

  const handleOpenCorrectionModal = msg => {
    setSelectQA(msg);
    setIsShowCorrectionModal(true);
  };

  const handleGetMoreCorrectionList = async () => {
    return dispatch(getCorrectionLisContinue(orgId, id));
  };

  useEffect(() => {
    Promise.resolve(dispatch(getPoints(orgId))).then(point => {
      setTotalPoints(point);
    });

    return () => {
      setChatInput('');
      setMessages([]);
      setIsSimulatimg(false);
    };
  }, []);

  useEffect(() => {
    if (isShowCorrectionRecords) {
      dispatch(getCorrectionListInit(orgId, id));
    }
  }, [isShowCorrectionRecords]);

  return (
    <>
      <div className={classes.root}>
        <div
          style={{
            width: isShowCorrectionRecords ? 'calc(100% - 485px)' : '100%',
          }}
        >
          <div className={classes.dfc}>
            <PageTitle title={t(`${pathKey}:testing`)} />
            <div
              className={classes.correctionRecords}
              onClick={() => {
                setIsShowCorrectionRecords(true);
              }}
            >
              {t(`${pathKey}:correctionRecords`)}
            </div>
          </div>
          <div className={classes.chatBlock}>
            <div className={classes.chatHeader}>
              <div className={classes.title}>
                {t(`${pathKey}:testingContainer.title`)}
                <Tooltip
                  light
                  placement="top"
                  title={t(`${pathKey}:testingContainer.titleTooltip`)}
                >
                  <img src={icHelpCircle} alt="plan is expired" />
                </Tooltip>
              </div>
              <div
                className={classnames(classes.clear, {
                  [classes.notAllowed]: isSimulatimg || !isDataVerify,
                })}
                onClick={handleClear}
              >
                {t(`${pathKey}:testingContainer.clear`)}
                <IconDelete
                  className={classes.delete}
                  id="icon-delete-messages"
                  width={20}
                  height={20}
                  color={color.$7}
                  hoverColor={color.$7}
                />
              </div>
            </div>
            <div className={classes.chatContent}>
              <MessageContent
                data={data}
                messages={messages}
                onOpenCorrectionModal={handleOpenCorrectionModal}
              />
              <div className={classes.chatInput}>
                {isSimulatimg && (
                  <div className={classes.aiLoading}>
                    {t(`${pathKey}:testingContainer.aiLoading`)}
                  </div>
                )}
                <TextField
                  inputRef={chartInputRef}
                  className={classes.input}
                  isValid
                  multiline
                  disabled={isSimulatimg || !isDataVerify}
                  minRows={1}
                  maxRows={4}
                  onChange={textChangeHandler}
                  keyPress={e => {
                    if (e.key === 'Enter' && e.shiftKey) {
                      // TODO: do nothing no remove this line
                    } else if (e.key === 'Enter' && chatInput !== '') {
                      submitHandler();
                      e.preventDefault();
                    } else if (e.key === 'Enter' && chatInput === '') {
                      e.preventDefault();
                    }
                  }}
                  value={chatInput}
                  placeholder={t(
                    `${pathKey}:testingContainer.inputPlaceholder`,
                  )}
                  type="text"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment
                        position="end"
                        className={classes.endAdornment}
                      >
                        {!isSimulatimg && isDataVerify && (
                          <EmojiPicker onClick={onEmojiClick} />
                        )}
                      </InputAdornment>
                    ),
                  }}
                  onFocus={() => {}}
                />
                <div className={classes.point}>
                  {t(`${pathKey}:testingContainer.point1`)}
                  <span>{`${TWD(totalPoints, false).format()}`}</span>
                  {t(`${pathKey}:testingContainer.point2`)}
                  <a
                    href="/"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={() => {
                      window.open(
                        `mailto:${config.ADDON_UNAVAIABLE_MAIL}?subject=${t(
                          `${pathKey}:emailSubject`,
                        )}&body=${encodeURIComponent(
                          t(`${pathKey}:emailBody`, {
                            orgId,
                            displayName,
                          }),
                        )}`,
                      );
                    }}
                  >
                    {t(`${pathKey}:testingContainer.point3`)}
                  </a>
                  {t(`${pathKey}:testingContainer.point4`)}
                </div>
                <div
                  className={classnames(classes.point, {
                    [classes.red]: tuningUsed > quota,
                  })}
                >
                  {t(`${pathKey}:basicSettingContainer.totalTuningCount`)}
                  <span>{`${TWD(tuningUsed, false).format()}/${TWD(
                    quota,
                    false,
                  ).format()}`}</span>
                  {t(`${pathKey}:basicSettingContainer.totalTuningCount1`)}
                  <a
                    href="/"
                    rel="noopener noreferrer"
                    target="_blank"
                    onClick={() => {
                      window.open(
                        `mailto:${config.ADDON_UNAVAIABLE_MAIL}?subject=${t(
                          `${pathKey}:aiTuningEmailSubject`,
                        )}&body=${encodeURIComponent(
                          t(`${pathKey}:aiTuningEmailBody`, {
                            orgId,
                            displayName,
                          }),
                        )}`,
                      );
                    }}
                  >
                    {t(`${pathKey}:basicSettingContainer.totalTuningCount2`)}
                  </a>
                  ？）
                </div>
                <div className={classes.tip}>
                  {t(`${pathKey}:testingContainer.tip`)}
                </div>
              </div>
            </div>
          </div>
        </div>
        {isShowCorrectionRecords && (
          <CorrectionRecords
            total={tuningUsed}
            quota={quota}
            list={correctionList}
            isLoading={isLoading}
            onMore={handleGetMoreCorrectionList}
            onClose={() => {
              setIsShowCorrectionRecords(false);
            }}
            onDelete={handleDeleteCorrection}
            onOpenCorrectionModal={handleOpenCorrectionModal}
          />
        )}
      </div>
      <CorrectionModal
        data={selectQA}
        isOpen={isShowCorrectionModal}
        onClose={() => setIsShowCorrectionModal(false)}
        onChange={handleAddCorrection}
      />
    </>
  );
};

export default TestingContainer;
